import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthProvider";

import StatsCard from "../../components/StatsCard";
import CompactStatsCard from "../../components/CompactStatsCard";
import Loader from "../../components/Loader";
import { STUDENT_LEVELS } from "../../utils";

import { Badge, Button, Card, Form, Tabs, Tab, ListGroup, Modal } from "react-bootstrap";
import { Clock, Wrench, BookOpen, CheckCircle, Download, XIcon } from "lucide-react";
import { formatDistanceToNow, set } from "date-fns";
import { toast } from "react-toastify";

import {
  getAssignedTasksByUserId,
  updateTaskStatus,
} from "../../services/taskService";
import { publicHolidays } from "../../data/publicHolidays";
import { getClassesByUserId } from "../../services/classService";
import Classes from "../Classes";
import LearningMaterials from "../../components/LearningMaterials";
import TaskDescriptionView from "../../components/TaskDescriptionView";
import { getRelevantActivities, logActivity } from "../../services/activityService";
import { getSkills } from "../../services/skillService";
import { updateUser } from "../../services/userService";
import { getSalaryConfigByUserId, getSalaryRecordsByUserId } from "../../services/salaryService";
import {
  checkIn,
  checkOut,
  getTodayAttendance,
  getUserAttendance,
  getUserAttendanceForMonth,
} from "../../services/attendanceService";
import {
  getLeavesByUserId,
  getLeaveStats,
  applyForLeave,
  cancelLeave,
  LEAVE_TYPES,
  LEAVE_STATUS,
  calculateLeaveDays,
} from "../../services/leaveService";
import {
  getProjectsByStudentId,
  PROJECT_STATUS,
  PROJECT_PRIORITY,
} from "../../services/projectService";
import {
  getStudentFeeRecord,
  getStudentPaymentHistory,
  PAYMENT_STATUS,
  formatCurrency as formatFeeCurrency,
  getPaymentStatusColor,
  getPaymentStatusDisplay,
} from "../../services/studentFeeService";
import { getMaterials, materialMatchesAudience } from "../../services/materialService";

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Public Holidays logic
  const [upcomingHoliday, setUpcomingHoliday] = useState(null);
  const [todayHoliday, setTodayHoliday] = useState(null);
  // Remove unused allHolidays and showHolidayModal

  useEffect(() => {
    // Filter holidays for current year
    const year = new Date().getFullYear();
    const holidays = publicHolidays.filter(h => h.date.startsWith(year.toString()));

    // Show info message one day before a holiday and on the holiday
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().slice(0, 10);
    const foundTomorrow = holidays.find(h => h.date === tomorrowStr);
    const foundToday = holidays.find(h => h.date === todayStr);
    setUpcomingHoliday(foundTomorrow || null);
    setTodayHoliday(foundToday || null);
  }, []);
  const [stats, setStats] = useState([]);
  const [courses, setCourses] = useState([]);
  const [skills, setSkills] = useState(user?.skills || []);
  const [tasks, setTasks] = useState([]);
  const [selectedTaskDetails, setSelectedTaskDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newSkill, setNewSkill] = useState("");
  const [classes, setClasses] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [allSkills, setAllSkills] = useState([]);
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [monthlyAttendance, setMonthlyAttendance] = useState([]);
  const [attendanceMonth, setAttendanceMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  // Fetch monthly attendance
  const fetchMonthlyAttendance = async () => {
    if (user?.id && attendanceMonth) {
      const [year, month] = attendanceMonth.split("-").map(Number);
      try {
        const records = await getUserAttendanceForMonth(user.id, month, year);
        setMonthlyAttendance(records);
      } catch (e) {
        setMonthlyAttendance([]);
      }
    }
  };
  const [attendanceLoading, setAttendanceLoading] = useState(false);

  // Leave management states
  const [leaveStats, setLeaveStats] = useState(null);
  const [userLeaves, setUserLeaves] = useState([]);
  const [showQuickLeaveForm, setShowQuickLeaveForm] = useState(false);
  const [quickLeaveLoading, setQuickLeaveLoading] = useState(false);
  const [cancelingLeaveId, setCancelingLeaveId] = useState(null);
  const [leaveFormData, setLeaveFormData] = useState({
    leaveType: LEAVE_TYPES.PERSONAL,
    startDate: "",
    endDate: "",
    reason: "",
  });

  // Project management states
  const [projects, setProjects] = useState([]);
  const [projectStats, setProjectStats] = useState({
    total: 0,
    inProgress: 0,
    completed: 0,
    pending: 0,
  });

  // Salary management states (for employees)
  const [salaryConfig, setSalaryConfig] = useState(null);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);

  // Fee management states (for paid trainees and above)
  const [feeRecord, setFeeRecord] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [feeLoading, setFeeLoading] = useState(false);

  const fetchMaterials = async () => {
    try {
      const mats = await getMaterials();
      const studentMaterials = (mats || []).filter((material) =>
        materialMatchesAudience(material, "student")
      );
      setMaterials(filterMaterialsByStudentSkills(studentMaterials, skills));
    } catch (error) {
      console.error("Error loading materials:", error);
      setMaterials([]);
    }
  };

  const getSkillId = (skill) => (typeof skill === "string" ? skill : skill?.id);
  const getSkillName = (skill) =>
    typeof skill === "string" ? skill : skill?.name || skill?.id || "";
  const normalizeSkillValue = (value) => String(value || "").trim().toLowerCase();

  const filterMaterialsByStudentSkills = (materialList, studentSkills) => {
    const studentSkillKeys = new Set(
      (studentSkills || []).flatMap((skill) => [
        normalizeSkillValue(getSkillId(skill)),
        normalizeSkillValue(getSkillName(skill)),
      ]).filter(Boolean)
    );

    if (studentSkillKeys.size === 0) return [];

    return materialList.filter((material) => {
      const materialSkills = Array.isArray(material.skills) ? material.skills : [];
      const materialSkillIds = Array.isArray(material.skillIds) ? material.skillIds : [];

      if (materialSkills.length === 0 && materialSkillIds.length === 0) {
        return true;
      }

      return [...materialSkills, ...materialSkillIds].some((skill) =>
        studentSkillKeys.has(normalizeSkillValue(getSkillId(skill))) ||
        studentSkillKeys.has(normalizeSkillValue(getSkillName(skill)))
      );
    });
  };

  useEffect(() => {
    fetchStudentData();
    fetchStudentClasses();
    fetchAdminActivities();
    fetchAllSkills();
    fetchLeaveData();
    fetchProjectData();
    fetchMaterials();
    if (user?.studentLevel === STUDENT_LEVELS.EMPLOYEE || user?.studentLevel === STUDENT_LEVELS.INTERN) {
      fetchTodayAttendance();
      fetchAttendanceHistory();
      fetchMonthlyAttendance();
      if (user?.studentLevel === STUDENT_LEVELS.EMPLOYEE) {
        fetchSalaryInfo();
      }
    }
    // Fetch fee information for paid trainees and above
    if (user?.studentLevel === STUDENT_LEVELS.PAID_TRAINEE ||
      user?.studentLevel === STUDENT_LEVELS.INTERN ||
      user?.studentLevel === STUDENT_LEVELS.EMPLOYEE) {
      fetchFeeInfo();
    }
  }, [user?.id, user?.studentLevel, attendanceMonth]);
  // --- Monthly Attendance Table ---
  const renderMonthlyAttendanceTable = () => (
    <div className="mb-4">
      <h5>Monthly Attendance</h5>
      <div className="mb-2">
        <label htmlFor="attendanceMonth">Select Month: </label>
        <input
          id="attendanceMonth"
          type="month"
          value={attendanceMonth}
          onChange={e => setAttendanceMonth(e.target.value)}
        />
      </div>
      <div className="table-responsive">
        <table className="table table-bordered table-sm">
          <thead>
            <tr>
              <th>Date</th>
              <th>Check In</th>
              <th>Check Out</th>
              <th>Hours</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {monthlyAttendance.length === 0 ? (
              <tr><td colSpan="5" className="text-center">No records</td></tr>
            ) : (
              monthlyAttendance.map((rec) => {
                const needsRegularise = !rec.checkIn || !rec.checkOut || (rec.totalHours || 0) < 8;
                return (
                  <tr key={rec.id}>
                    <td>{rec.date}</td>
                    <td>{rec.checkIn ? new Date(rec.checkIn).toLocaleTimeString() : "-"}</td>
                    <td>{rec.checkOut ? new Date(rec.checkOut).toLocaleTimeString() : "-"}</td>
                    <td>{rec.totalHours ? rec.totalHours.toFixed(2) : "-"}</td>
                    <td>
                      {needsRegularise ? (
                        <span className="text-danger">Irregular</span>
                      ) : (
                        <span className="text-success">OK</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Update main stats when leave data changes
  useEffect(() => {
    if (user?.id && leaveStats) {
      fetchStudentData();
    }
  }, [leaveStats]);

  // Update main stats when project data changes
  useEffect(() => {
    if (user?.id && projectStats) {
      fetchStudentData();
    }
  }, [projectStats]);

  const fetchAllSkills = async () => {
    const skillsList = await getSkills();
    setAllSkills(skillsList);
  };

  const fetchAdminActivities = async () => {
    if (user?.id && user?.role) {
      try {
        const activities = await getRelevantActivities(user.id, user.role, 10);
        setRecentActivity(activities);
      } catch (error) {
        console.error("Error fetching relevant activities:", error);
        setRecentActivity([]);
      }
    }
  };

  const fetchStudentClasses = async () => {
    if (user?.id) {
      const classes = await getClassesByUserId(user.id);
      setClasses(classes);
    }
  };

  const fetchStudentData = async () => {
    setLoading(true);

    if (user?.id) {
      const courses = user.courses || [];
      const tasks = await getAssignedTasksByUserId(user.id);

      setCourses(courses);
      setTasks(tasks);

      // Base stats
      const baseStats = [
        {
          title: "Assigned Tasks",
          value: tasks.length,
          color: "bg-indigo-500",
        },
        {
          title: "Pending Tasks",
          value: tasks.filter((t) => t.status === "pending").length,
          color: "bg-pink-500",
        },
        {
          title: "Completed Tasks",
          value: tasks.filter((t) => t.status === "completed").length,
          color: "bg-green-500",
        },
      ];

      // Add leave stats if available
      if (leaveStats) {
        baseStats.push({
          title: "Leave Applications",
          value: leaveStats.total || 0,
          color: "bg-blue-500",
        });
      }

      // Add project stats if available
      if (projectStats && projectStats.total > 0) {
        baseStats.push({
          title: "Assigned Projects",
          value: projectStats.total,
          color: "bg-purple-500",
        });
      }

      setStats(baseStats);
    }

    setLoading(false);
  };

  const fetchTodayAttendance = async () => {
    if (user?.id) {
      try {
        const attendance = await getTodayAttendance(user.id);
        setTodayAttendance(attendance);
      } catch (error) {
        console.error("Error fetching today's attendance:", error);
      }
    }
  };

  const fetchAttendanceHistory = async () => {
    if (user?.id) {
      try {
        const history = await getUserAttendance(user.id, 7); // Last 7 days
        setAttendanceHistory(history);
      } catch (error) {
        console.error("Error fetching attendance history:", error);
      }
    }
  };

  const fetchLeaveData = async () => {
    if (user?.id) {
      try {
        const [leaves, stats] = await Promise.all([
          getLeavesByUserId(user.id),
          getLeaveStats(user.id),
        ]);
        setUserLeaves(leaves);
        setLeaveStats(stats);
      } catch (error) {
        console.error("Error fetching leave data:", error);
      }
    }
  };

  const fetchProjectData = async () => {
    if (user?.id) {
      try {
        const userProjects = await getProjectsByStudentId(user.id);
        setProjects(userProjects);

        // Calculate project statistics
        const stats = {
          total: userProjects.length,
          inProgress: userProjects.filter(
            (p) => p.status === PROJECT_STATUS.IN_PROGRESS
          ).length,
          completed: userProjects.filter(
            (p) => p.status === PROJECT_STATUS.COMPLETED
          ).length,
          pending: userProjects.filter(
            (p) => p.status === PROJECT_STATUS.PLANNING
          ).length,
        };
        setProjectStats(stats);
      } catch (error) {
        console.error("Error fetching project data:", error);
      }
    }
  };

  const fetchSalaryInfo = async () => {
    if (user?.id) {
      setSalaryLoading(true);
      try {
        const [config, history] = await Promise.all([
          getSalaryConfigByUserId(user.id),
          getSalaryRecordsByUserId(user.id, 6), // Last 6 months
        ]);
        setSalaryConfig(config);
        setSalaryHistory(history);
      } catch (error) {
        console.error("Error fetching salary info:", error);
        setSalaryConfig(null);
        setSalaryHistory([]);
      }
      setSalaryLoading(false);
    }
  };

  const fetchFeeInfo = async () => {
    if (user?.id) {
      setFeeLoading(true);
      try {
        const [fee, payments] = await Promise.all([
          getStudentFeeRecord(user.id),
          getStudentPaymentHistory(user.id),
        ]);
        setFeeRecord(fee);
        setPaymentHistory(payments);
      } catch (error) {
        console.error("Error fetching fee info:", error);
        setFeeRecord(null);
        setPaymentHistory([]);
      }
      setFeeLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setAttendanceLoading(true);
    try {
      await checkIn(user.id, user.name, user.role);
      await fetchTodayAttendance();
      toast.success("Checked in successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to check in");
    }
    setAttendanceLoading(false);
  };

  const handleCheckOut = async () => {
    setAttendanceLoading(true);
    try {
      await checkOut(user.id, user.role);
      await fetchTodayAttendance();
      await fetchAttendanceHistory();
      toast.success("Checked out successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to check out");
    }
    setAttendanceLoading(false);
  };

  const handleAddSkill = async () => {
    if (!skills.includes(newSkill)) {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        toast.error("User not found.");
        setLoading(false);
        return;
      }

      user["skills"] = [...skills, newSkill];
      const updatedSkill = await updateUser(user.id, user);
      localStorage.setItem("user", JSON.stringify(user));
      if (updatedSkill) {
        // Log activity
        await logActivity({
          userId: user.id,
          userRole: user.role,
          text: `${user.name || user.email} added skill: ${newSkill?.name || newSkill}`,
          type: "skill",
          actionType: "student_action",
          metadata: {
            userName: user.name || user.email,
            skillName: newSkill?.name || newSkill,
            skillId: newSkill?.id || newSkill,
            action: "add"
          }
        });

        setLoading(false);
        setSkills([...skills, newSkill]);
        setNewSkill("");
        toast.success("Skill added successfully.");

        // Refresh activities
        fetchAdminActivities();
      }
    } else {
      setLoading(false);
      toast.error("Skill is already added.");
    }
  };

  const handleRemoveSkill = async (skillId) => {
    if (window.confirm("Are you sure you want to remove this skill?")) {
      const skillToRemove = skills.find((s) => s.id === skillId);
      const updated = skills.filter((s) => s.id !== skillId);
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) {
        toast.error("User not found.");
        setLoading(false);
        return;
      }

      user["skills"] = updated;
      const deletedSkill = await updateUser(user.id, user);
      localStorage.setItem("user", JSON.stringify(user));
      if (deletedSkill) {
        // Log activity
        await logActivity({
          userId: user.id,
          userRole: user.role,
          text: `${user.name || user.email} removed skill: ${skillToRemove?.name || skillToRemove}`,
          type: "skill",
          actionType: "student_action",
          metadata: {
            userName: user.name || user.email,
            skillName: skillToRemove?.name || skillToRemove,
            skillId: skillToRemove?.id || skillId,
            action: "remove"
          }
        });

        setSkills(updated);
        setLoading(false);
        toast.success("Skill removed successfully.");

        // Refresh activities
        fetchAdminActivities();
      } else {
        setLoading(false);
        toast.error("Failed to remove skill.");
      }
    }
  };

  const handleTaskStatusChange = async (taskId, newStatus) => {
    try {
      setLoading(true);
      // Pass true as third parameter because these are assigned tasks
      await updateTaskStatus(taskId, newStatus, true);

      // Log activity for task status change
      const task = tasks.find(t => t.id === taskId);
      const user = JSON.parse(localStorage.getItem("user"));

      if (user && task) {
        await logActivity({
          userId: user.id,
          userRole: user.role,
          text: `${user.name || user.email} changed task "${task.taskName}" status to ${newStatus.replace('_', ' ')}`,
          type: "task",
          actionType: "student_action",
          metadata: {
            userName: user.name || user.email,
            taskId: taskId,
            taskName: task.taskName,
            oldStatus: task.status,
            newStatus: newStatus,
            action: "status_change"
          }
        });
      }

      // Refresh data to show updated status
      await fetchStudentData();
      await fetchAdminActivities();

      toast.success(`Task status updated to ${newStatus.replace('_', ' ')}`);
      setLoading(false);
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.error("Failed to update task status");
      setLoading(false);
    }
  };

  const handleQuickLeaveSubmit = async (e) => {
    e.preventDefault();

    if (
      !leaveFormData.startDate ||
      !leaveFormData.endDate ||
      !leaveFormData.reason.trim()
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (new Date(leaveFormData.startDate) > new Date(leaveFormData.endDate)) {
      toast.error("End date must be after start date");
      return;
    }

    setQuickLeaveLoading(true);
    try {
      const leaveData = {
        ...leaveFormData,
        userId: user.id,
        userName: user.name || user.email,
        userEmail: user.email,
        totalDays: calculateLeaveDays(
          leaveFormData.startDate,
          leaveFormData.endDate
        ),
      };

      await applyForLeave(leaveData);

      await logActivity({
        userId: user.id,
        userRole: user.role,
        text: `${user.name || user.email} applied for leave (${leaveFormData.leaveType}) from ${leaveFormData.startDate} to ${leaveFormData.endDate}`,
        type: "leave",
        actionType: "student_action",
        metadata: {
          userName: user.name || user.email,
          action: "apply_leave",
          leaveType: leaveFormData.leaveType,
          startDate: leaveFormData.startDate,
          endDate: leaveFormData.endDate,
          reason: leaveFormData.reason
        }
      });

      // Reset form
      setLeaveFormData({
        leaveType: LEAVE_TYPES.PERSONAL,
        startDate: "",
        endDate: "",
        reason: "",
      });
      setShowQuickLeaveForm(false);

      // Refresh leave data
      await fetchLeaveData();
    } catch (error) {
      console.error("Error submitting leave application:", error);
    }
    setQuickLeaveLoading(false);
  };

  const handleCancelLeave = async (leaveId) => {
    setCancelingLeaveId(leaveId);
    try {
      await cancelLeave(leaveId);
      await fetchLeaveData();
    } catch (error) {
      console.error("Error cancelling leave:", error);
    }
    setCancelingLeaveId(null);
  };

  const handleLeaveFormChange = (e) => {
    const { name, value } = e.target;
    setLeaveFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const getIcon = (type) => {
    switch (type) {
      case "skill":
        return <Wrench size={16} className="text-blue-500" />;
      case "task":
        return <CheckCircle size={16} className="text-green-500" />;
      case "material":
        return <BookOpen size={16} className="text-purple-500" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getActivityDate = (activity) => {
    const timestamp = activity.createdAt || activity.time;

    if (!timestamp) return null;
    if (timestamp instanceof Date) return timestamp;
    if (typeof timestamp === "number" || typeof timestamp === "string") {
      return new Date(timestamp);
    }
    if (typeof timestamp.toDate === "function") return timestamp.toDate();
    if (timestamp.seconds) return new Date(timestamp.seconds * 1000);

    return null;
  };

  return (
    <div className="p-4">
      {/* Public Holidays Info Banner and Button Side by Side */}
      {(todayHoliday || upcomingHoliday) && (
        <div className="d-flex align-items-center mb-3" style={{ gap: '1rem', flexWrap: 'nowrap' }}>
          {todayHoliday && (
            <div className="alert alert-success mb-0 flex-grow-1 d-flex align-items-center" style={{ minHeight: '48px' }}>
              <span><strong>Today is a public holiday:</strong> <b>{todayHoliday.name}</b> ({todayHoliday.date}). Enjoy your holiday!</span>
            </div>
          )}
          {!todayHoliday && upcomingHoliday && (
            <div className="alert alert-info mb-0 flex-grow-1 d-flex align-items-center" style={{ minHeight: '48px' }}>
              <span><strong>Info:</strong> Tomorrow is <b>{upcomingHoliday.name}</b> ({upcomingHoliday.date}). Enjoy your holiday!</span>
            </div>
          )}
          <Button
            variant="outline-info"
            size="sm"
            className="ms-auto"
            style={{ minWidth: '170px', whiteSpace: 'nowrap' }}
            onClick={() => navigate("/holidays")}
          >
            View Public Holidays
          </Button>
        </div>
      )}
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-2xl font-bold mb-2">
          👋 Welcome {user?.name || "Student"}
        </h2>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600 dark:text-white">
              Level:
            </span>
            <Badge
              bg={
                user?.studentLevel === STUDENT_LEVELS.FREE_TRAINEE
                  ? "secondary"
                  : user?.studentLevel === STUDENT_LEVELS.PAID_TRAINEE
                    ? "primary"
                    : user?.studentLevel === STUDENT_LEVELS.INTERN
                      ? "warning"
                      : user?.studentLevel === STUDENT_LEVELS.EMPLOYEE
                        ? "success"
                        : "secondary"
              }
              className="px-3 py-2"
            >
              {user?.studentLevel
                ? user.studentLevel.replace("_", " ").toUpperCase()
                : "FREE TRAINEE"}
            </Badge>
          </div>
          {user?.batches && user.batches.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-600 dark:text-white">
                Batches:
              </span>
              <div className="flex gap-1">
                {user.batches.map((batch) => (
                  <Badge bg="info" className="px-3 py-2" key={batch.id}>
                    {batch.name}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {loading && <Loader />}

      {/* Compact Overview Stats */}
      <div className="row mb-4">
        {/* Tasks Stats */}
        <div className="col-lg-4 col-md-6 mb-3">
          <CompactStatsCard
            icon="✅"
            title="Tasks"
            total={tasks.length}
            primaryColor="#6366F1"
            stats={[
              {
                value: tasks.filter((t) => t.status === "pending").length,
                label: "Pending",
                colorClass: "text-danger",
              },
              {
                value: tasks.filter((t) => t.status === "in_progress").length,
                label: "Progress",
                colorClass: "text-warning",
              },
              {
                value: tasks.filter((t) => t.status === "completed").length,
                label: "Done",
                colorClass: "text-success",
              },
            ]}
          />
        </div>

        {/* Projects Stats */}
        <div className="col-lg-4 col-md-6 mb-3">
          <CompactStatsCard
            icon="📂"
            title="Projects"
            total={projectStats.total}
            primaryColor="#8B5CF6"
            stats={[
              {
                value: projectStats.pending,
                label: "Planning",
                colorClass: "text-warning",
              },
              {
                value: projectStats.inProgress,
                label: "Progress",
                colorClass: "text-primary",
              },
              {
                value: projectStats.completed,
                label: "Done",
                colorClass: "text-success",
              },
            ]}
          />
        </div>

        {/* Leaves Stats */}
        {leaveStats && (
          <div className="col-lg-4 col-md-6 mb-3">
            <CompactStatsCard
              icon="🏖️"
              title="Leaves"
              total={leaveStats.total || 0}
              primaryColor="#10B981"
              stats={[
                {
                  value: leaveStats.pending || 0,
                  label: "Pending",
                  colorClass: "text-warning",
                },
                {
                  value: leaveStats.approved || 0,
                  label: "Approved",
                  colorClass: "text-success",
                },
                {
                  value: leaveStats.rejected || 0,
                  label: "Rejected",
                  colorClass: "text-danger",
                },
                {
                  value: leaveStats.totalDaysApproved || 0,
                  label: "Days Approved",
                  colorClass: "text-info",
                },
              ]}
            />
          </div>
        )}
      </div>

      {/* Attendance Section for Employee and Intern Level */}
      {(user?.studentLevel === STUDENT_LEVELS.EMPLOYEE || user?.studentLevel === STUDENT_LEVELS.INTERN) && (
        <Card className="mb-4 shadow">
          <Card.Body>
            <div className="flex justify-between items-center mb-3">
              <Card.Title className="mb-0">📅 Attendance</Card.Title>
              <div className="flex gap-2">
                {!todayAttendance && (
                  <Button
                    variant="success"
                    onClick={handleCheckIn}
                    disabled={attendanceLoading}
                  >
                    {attendanceLoading ? "Checking In..." : "Check In"}
                  </Button>
                )}
                {todayAttendance && !todayAttendance.checkOut && (
                  <Button
                    variant="danger"
                    onClick={handleCheckOut}
                    disabled={attendanceLoading}
                  >
                    {attendanceLoading ? "Checking Out..." : "Check Out"}
                  </Button>
                )}
              </div>
            </div>

            {todayAttendance && (
              <div className="bg-light p-3 rounded">
                <div className="row">
                  <div className="col-md-4">
                    <strong>Check In:</strong>{" "}
                    {todayAttendance.checkIn
                      ? new Date(todayAttendance.checkIn).toLocaleTimeString()
                      : "N/A"}
                  </div>
                  <div className="col-md-4">
                    <strong>Check Out:</strong>{" "}
                    {todayAttendance.checkOut
                      ? new Date(todayAttendance.checkOut).toLocaleTimeString()
                      : "Not checked out"}
                  </div>
                  <div className="col-md-4">
                    <strong>Hours:</strong>{" "}
                    {todayAttendance.totalHours
                      ? `${todayAttendance.totalHours} hrs`
                      : "In progress..."}
                  </div>
                </div>
              </div>
            )}

            {!todayAttendance && (
              <div className="text-muted">
                No attendance recorded for today. Click "Check In" to start your
                workday.
              </div>
            )}

            {attendanceHistory.length > 0 && (
              <div className="mt-3">
                <h6>Recent Attendance (Last 7 days):</h6>
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Check In</th>
                        <th>Check Out</th>
                        <th>Hours</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendanceHistory.slice(0, 7).map((record) => (
                        <tr key={record.id}>
                          <td>{record.date}</td>
                          <td>
                            {record.checkIn
                              ? new Date(record.checkIn).toLocaleTimeString()
                              : "N/A"}
                          </td>
                          <td>
                            {record.checkOut
                              ? new Date(record.checkOut).toLocaleTimeString()
                              : "N/A"}
                          </td>
                          <td>
                            {record.totalHours
                              ? `${record.totalHours} hrs`
                              : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Recent Activity Section (outside tabs) */}
      <Card className="shadow mt-3 mb-4">
        <Card.Body>
          <Card.Title>🕓 Timeline</Card.Title>
          {recentActivity.length === 0 ? (
            <p className="text-center text-muted">No timeline data found.</p>
          ) : (
            <ListGroup>
              {recentActivity.slice(0, 5).map((activity) => (
                <ListGroup.Item key={activity.id}>
                  <div className="flex gap-3 items-center">
                    {getIcon(activity.type)}
                    <span>{activity.text}</span>
                    <small className="text-muted">
                      {getActivityDate(activity) ? (
                        formatDistanceToNow(getActivityDate(activity), {
                          addSuffix: true,
                        })
                      ) : (
                        <span className="text-red-600 animate-pulse font-medium">
                          A few seconds ago
                        </span>
                      )}
                    </small>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      <div className="grid grid-cols-2 gap-4 mb-3">
        {/* Courses */}
        <Card className="mb-2 shadow">
          <Card.Body>
            <Card.Title className="mb-3">
              📚 Courses ({courses.length})
            </Card.Title>
            <div className="flex flex-wrap gap-2 mt-2">
              {courses.length > 0 ? (
                courses.map((c) => (
                  <Badge
                    bg="secondary"
                    className="p-2"
                    style={{ fontSize: "0.9rem" }}
                    key={c.id}
                  >
                    {c.name}
                  </Badge>
                ))
              ) : (
                <p>No courses assigned yet.</p>
              )}
            </div>
          </Card.Body>
        </Card>
        {/* Skills */}
        <Card className="mb-2 shadow">
          <Card.Body>
            <Card.Title>
              <div className="flex justify-between">
                <div>🛠 Skills ({skills.length})</div>
                <div className="flex gap-2">
                  <Form.Select
                    value={newSkill?.id || ""}
                    onChange={(e) => {
                      const selected = allSkills.find(
                        (skill) => skill.id === e.target.value
                      );
                      setNewSkill(selected);
                    }}
                  >
                    <option value="" disabled>
                      Select
                    </option>
                    {allSkills.map((skill) => (
                      <option key={skill.id} value={skill.id}>
                        {skill.name}
                      </option>
                    ))}
                  </Form.Select>
                  <Button onClick={handleAddSkill}>Add</Button>
                </div>
              </div>
            </Card.Title>
            <div className="flex flex-wrap gap-2 mt-2 mb-3">
              {skills.map((skill, idx) => (
                <Badge
                  key={idx}
                  bg="info"
                  className="p-2 flex items-center gap-2"
                  onClick={() => handleRemoveSkill(skill?.id)}
                >
                  <div className="flex items-center gap-2">
                    <span>{skill?.name}</span>
                    <XIcon
                      className="w-[18px] h-[18px] p-1 bg-blue-700 rounded-full cursor-pointer hover:bg-blue-900"
                      title="Remove Skill"
                    />
                  </div>
                </Badge>
              ))}
            </div>
          </Card.Body>
        </Card>
      </div>

      <Tabs
        defaultActiveKey="classes"
        id="student-dashboard-tabs"
        className="mb-4"
        variant="pills"
        style={{ background: '#f8fafc', borderRadius: 12, padding: '0.5rem 1rem' }}
      >
        <Tab
          eventKey="classes"
          title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><BookOpen size={18} /> Classes</span>}
        >
          <Classes canAdd={false} />
        </Tab>

        <Tab
          eventKey="tasks"
          title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><CheckCircle size={18} /> Tasks{tasks.length > 0 ? ` (${tasks.length})` : ''}</span>}
        >
          {/* Tasks */}
          <Card className="shadow">
            <Card.Body>
              <Card.Title>✅ My Tasks ({tasks.length})</Card.Title>
              {tasks.length === 0 ? (
                <p className="text-muted text-center">No tasks assigned.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Project</th>
                        <th>Image</th>
                        <th>Assignee</th>
                        <th>Due Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => (
                        <tr key={task.id}>
                          <td>{task.taskName}</td>
                          <td style={{ maxWidth: '300px' }}>
                            <div
                              style={{
                                maxHeight: "112px",
                                overflow: "hidden",
                              }}
                            >
                              <TaskDescriptionView text={task.taskDescription} preview />
                            </div>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 mt-1"
                              onClick={() => setSelectedTaskDetails(task)}
                            >
                              View details
                            </Button>
                          </td>
                          <td>
                            {task.projectName ? (
                              <Badge bg="info" className="text-white">
                                {task.projectName}
                              </Badge>
                            ) : (
                              <span className="text-muted">No Project</span>
                            )}
                          </td>
                          <td>
                            {task.taskImageUrl ? (
                              <Button
                                as="a"
                                href={task.taskImageUrl}
                                target="_blank"
                                rel="noreferrer"
                                download={task.taskImageName || "task-image"}
                                variant="outline-primary"
                                size="sm"
                                className="d-inline-flex align-items-center gap-1"
                              >
                                <Download size={14} />
                                Download
                              </Button>
                            ) : (
                              <span className="text-muted">No Image</span>
                            )}
                          </td>
                          <td>{task.assigneeName}</td>
                          <td>
                            {new Date(task.dueDate).toDateString()}
                          </td>
                          <td>
                            <Form.Select
                              size="sm"
                              value={task.status}
                              onChange={(e) =>
                                handleTaskStatusChange(task.id, e.target.value)
                              }
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                            </Form.Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab
          eventKey="leaves"
          title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Wrench size={18} /> Leaves</span>}
        >
          <div className="row">
            {/* Quick Leave Application */}
            <div className="col-md-6">
              <Card className="shadow h-100">
                <Card.Header>
                  <div className="d-flex justify-content-between align-items-center">
                    <h6 className="mb-0">📝 Quick Leave Application</h6>
                    <Button
                      size="sm"
                      variant={
                        showQuickLeaveForm ? "outline-secondary" : "primary"
                      }
                      onClick={() => setShowQuickLeaveForm(!showQuickLeaveForm)}
                    >
                      {showQuickLeaveForm ? "Cancel" : "Apply Leave"}
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body>
                  {showQuickLeaveForm ? (
                    <Form onSubmit={handleQuickLeaveSubmit}>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <Form.Label>Leave Type</Form.Label>
                          <Form.Select
                            name="leaveType"
                            value={leaveFormData.leaveType}
                            onChange={handleLeaveFormChange}
                            required
                          >
                            <option value={LEAVE_TYPES.PERSONAL}>
                              Personal
                            </option>
                            <option value={LEAVE_TYPES.SICK}>Sick Leave</option>
                            <option value={LEAVE_TYPES.VACATION}>
                              Vacation
                            </option>
                            <option value={LEAVE_TYPES.EMERGENCY}>
                              Emergency
                            </option>
                            <option value={LEAVE_TYPES.OTHER}>Other</option>
                          </Form.Select>
                        </div>
                        <div className="col-md-6">
                          {leaveFormData.startDate && leaveFormData.endDate && (
                            <div>
                              <Form.Label>Total Days</Form.Label>
                              <div className="form-control-plaintext">
                                <Badge bg="info">
                                  {calculateLeaveDays(
                                    leaveFormData.startDate,
                                    leaveFormData.endDate
                                  )}{" "}
                                  days
                                </Badge>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="row mb-3">
                        <div className="col-md-6">
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="startDate"
                            value={leaveFormData.startDate}
                            onChange={handleLeaveFormChange}
                            min={new Date().toISOString().split("T")[0]}
                            required
                          />
                        </div>
                        <div className="col-md-6">
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="endDate"
                            value={leaveFormData.endDate}
                            onChange={handleLeaveFormChange}
                            min={
                              leaveFormData.startDate ||
                              new Date().toISOString().split("T")[0]
                            }
                            required
                          />
                        </div>
                      </div>
                      <Form.Group className="mb-3">
                        <Form.Label>Reason</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          name="reason"
                          value={leaveFormData.reason}
                          onChange={handleLeaveFormChange}
                          placeholder="Please provide a reason for your leave..."
                          required
                        />
                      </Form.Group>
                      <Button
                        type="submit"
                        variant="success"
                        className="me-2"
                        disabled={quickLeaveLoading}
                      >
                        {quickLeaveLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Submitting...
                          </>
                        ) : (
                          "Submit"
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="outline-secondary"
                        onClick={() => setShowQuickLeaveForm(false)}
                        disabled={quickLeaveLoading}
                      >
                        Cancel
                      </Button>
                    </Form>
                  ) : (
                    <div className="text-center text-muted py-4">
                      <p>
                        Click "Apply Leave" to submit a quick leave application.
                      </p>
                      <small>
                        For detailed applications, visit the main Leaves page.
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>

            {/* Recent Leave History */}
            <div className="col-md-6">
              <Card className="shadow h-100">
                <Card.Header>
                  <h6 className="mb-0">📋 Recent Leave Applications</h6>
                </Card.Header>
                <Card.Body>
                  {userLeaves.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <p>No leave applications found.</p>
                      <small>Your leave history will appear here.</small>
                    </div>
                  ) : (
                    <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                      {userLeaves.slice(0, 5).map((leave) => (
                        <div key={leave.id} className="border-bottom pb-2 mb-2">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong className="text-capitalize">
                                {leave.leaveType.replace("_", " ")}
                              </strong>
                              <br />
                              <small className="text-muted">
                                {new Date(leave.startDate).toLocaleDateString()}{" "}
                                - {new Date(leave.endDate).toLocaleDateString()}
                              </small>
                              <br />
                              <small className="text-info">
                                {calculateLeaveDays(
                                  leave.startDate,
                                  leave.endDate
                                )}{" "}
                                days
                              </small>
                            </div>
                            <div className="text-end">
                              <Badge
                                bg={
                                  leave.status === LEAVE_STATUS.APPROVED
                                    ? "success"
                                    : leave.status === LEAVE_STATUS.REJECTED
                                      ? "danger"
                                      : leave.status === LEAVE_STATUS.PENDING
                                        ? "warning"
                                        : "secondary"
                                }
                                className="mb-1"
                              >
                                {leave.status}
                              </Badge>
                              {leave.status === LEAVE_STATUS.PENDING && (
                                <div>
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleCancelLeave(leave.id)}
                                    disabled={cancelingLeaveId === leave.id}
                                  >
                                    {cancelingLeaveId === leave.id ? (
                                      <div className="flex items-center gap-1">
                                        <span
                                          className="spinner-border spinner-border-sm"
                                          role="status"
                                          aria-hidden="true"
                                        ></span>{" "}
                                        Canceling...
                                      </div>
                                    ) : (
                                      "Cancel"
                                    )}
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="mb-1 small text-muted">
                            {leave.reason.length > 60
                              ? leave.reason.substring(0, 60) + "..."
                              : leave.reason}
                          </p>
                          {leave.adminComments && (
                            <div className="mt-1">
                              <small className="text-success">
                                <strong>Admin:</strong> {leave.adminComments}
                              </small>
                            </div>
                          )}
                        </div>
                      ))}
                      {userLeaves.length > 5 && (
                        <small className="text-muted">
                          Showing latest 5 applications.{" "}
                          <a href="/leaves">View all</a>
                        </small>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>
        </Tab>

        <Tab
          eventKey="projects"
          title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={18} /> Projects{projects.length > 0 ? ` (${projects.length})` : ''}</span>}
        >
          {/* Projects */}
          <Card className="shadow">
            <Card.Body>
              <Card.Title>📂 My Projects ({projects.length})</Card.Title>

              {projects.length === 0 ? (
                <p>No projects assigned.</p>
              ) : (
                <div className="row">
                  {projects.map((project) => (
                    <div key={project.id} className="col-md-6 mb-3">
                      <Card className="h-100">
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-2">
                            <Card.Title className="h5">
                              {project.name}
                            </Card.Title>
                            <Badge
                              bg={
                                project.status === PROJECT_STATUS.COMPLETED
                                  ? "success"
                                  : project.status ===
                                    PROJECT_STATUS.IN_PROGRESS
                                    ? "primary"
                                    : project.status === PROJECT_STATUS.PLANNING
                                      ? "warning"
                                      : project.status === PROJECT_STATUS.REVIEW
                                        ? "info"
                                        : project.status === PROJECT_STATUS.ON_HOLD
                                          ? "secondary"
                                          : "danger"
                              }
                            >
                              {project.status.replace("_", " ").toUpperCase()}
                            </Badge>
                          </div>

                          <Card.Text className="text-muted mb-2">
                            {project.description}
                          </Card.Text>

                          <div className="mb-2">
                            <small className="text-muted">
                              <strong>Priority:</strong>
                              <Badge
                                bg={
                                  project.priority === PROJECT_PRIORITY.CRITICAL
                                    ? "danger"
                                    : project.priority === PROJECT_PRIORITY.HIGH
                                      ? "warning"
                                      : project.priority ===
                                        PROJECT_PRIORITY.MEDIUM
                                        ? "info"
                                        : "secondary"
                                }
                                className="ms-1"
                              >
                                {project.priority.toUpperCase()}
                              </Badge>
                            </small>
                          </div>

                          {project.dueDate && (
                            <div className="mb-2">
                              <small className="text-muted">
                                <Clock size={14} className="me-1" />
                                Due:{" "}
                                {new Date(
                                  project.dueDate.seconds * 1000
                                ).toLocaleDateString()}
                              </small>
                            </div>
                          )}

                          {project.tasks && project.tasks.length > 0 && (
                            <div className="mt-2">
                              <small className="text-muted">
                                <CheckCircle size={14} className="me-1" />
                                {project.tasks.length} task(s) assigned
                              </small>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </div>
                  ))}
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab
          eventKey="materials"
          title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><BookOpen size={18} /> Materials</span>}
        >
          <LearningMaterials materials={materials} />
        </Tab>

        {/* Fee Payment Tab - For paid trainees and above */}
        {(user?.studentLevel === STUDENT_LEVELS.PAID_TRAINEE ||
          user?.studentLevel === STUDENT_LEVELS.INTERN) && (
            <Tab
              eventKey="fees"
              title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><BookOpen size={18} /> Fee Payment</span>}
            >
              <Card className="shadow">
                <Card.Body>
                  <Card.Title>💳 My Fee Information</Card.Title>

                  {feeLoading ? (
                    <div className="text-center py-4">
                      <Loader />
                    </div>
                  ) : feeRecord ? (
                    <>
                      {/* Fee Summary */}
                      <div className="row mb-4">
                        <div className="col-md-4">
                          <Card className="h-100 border-primary">
                            <Card.Header className="bg-primary text-white">
                              <h6 className="mb-0">📊 Fee Summary</h6>
                            </Card.Header>
                            <Card.Body>
                              {feeRecord.discountAmount > 0 && (
                                <div className="mb-3">
                                  <div className="d-flex justify-content-between align-items-center">
                                    <strong>Original Fee:</strong>
                                    <span className="text-muted text-decoration-line-through">
                                      {formatFeeCurrency(feeRecord.originalFee)}
                                    </span>
                                  </div>
                                  <div className="d-flex justify-content-between align-items-center mt-1">
                                    <strong>Discount:</strong>
                                    <Badge bg="success">
                                      -{formatFeeCurrency(feeRecord.discountAmount)}
                                      ({feeRecord.discountPercentage?.toFixed(1)}%)
                                    </Badge>
                                  </div>
                                  {feeRecord.discountReason && (
                                    <small className="text-success">
                                      💡 {feeRecord.discountReason}
                                    </small>
                                  )}
                                </div>
                              )}
                              <div className="mb-3">
                                <strong>Total Fee:</strong>
                                <h4 className="text-primary mb-0">
                                  {formatFeeCurrency(feeRecord.totalFee)}
                                </h4>
                                {feeRecord.discountAmount > 0 && (
                                  <small className="text-success">After discount</small>
                                )}
                              </div>
                              <div className="mb-3">
                                <strong>Paid Amount:</strong>
                                <h5 className="text-success mb-0">
                                  {formatFeeCurrency(feeRecord.paidAmount)}
                                </h5>
                              </div>
                              <div className="mb-3">
                                <strong>Remaining Amount:</strong>
                                <h5 className="text-danger mb-0">
                                  {formatFeeCurrency(feeRecord.remainingAmount)}
                                </h5>
                              </div>
                              <div>
                                <strong>Payment Status:</strong>
                                <br />
                                <Badge
                                  bg={getPaymentStatusColor(feeRecord.paymentStatus)}
                                  className="mt-1"
                                >
                                  {getPaymentStatusDisplay(feeRecord.paymentStatus)}
                                </Badge>
                              </div>
                            </Card.Body>
                          </Card>
                        </div>

                        <div className="col-md-4">
                          <Card className="h-100 border-info">
                            <Card.Header className="bg-info text-white">
                              <h6 className="mb-0">📅 Installment Details</h6>
                            </Card.Header>
                            <Card.Body>
                              <div className="mb-3">
                                <strong>Installment Amount:</strong>
                                <h5 className="text-info mb-0">
                                  {formatFeeCurrency(feeRecord.installmentAmount)}
                                </h5>
                              </div>
                              <div className="mb-3">
                                <strong>Installments Paid:</strong>
                                <h5 className="text-success mb-0">
                                  {feeRecord.installmentsPaid} / {feeRecord.maxInstallments}
                                </h5>
                              </div>
                              <div className="mb-3">
                                <strong>Remaining Installments:</strong>
                                <h5 className="text-warning mb-0">
                                  {feeRecord.installmentsRemaining}
                                </h5>
                              </div>
                              {feeRecord.dueDate && feeRecord.paymentStatus !== PAYMENT_STATUS.FULLY_PAID && (
                                <div>
                                  <strong>Next Due Date:</strong>
                                  <br />
                                  <Badge
                                    bg={new Date(feeRecord.dueDate) < new Date() ? "danger" : "warning"}
                                    className="mt-1"
                                  >
                                    {new Date(feeRecord.dueDate).toLocaleDateString()}
                                  </Badge>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </div>

                        <div className="col-md-4">
                          <Card className="h-100 border-success">
                            <Card.Header className="bg-success text-white">
                              <h6 className="mb-0">📈 Progress</h6>
                            </Card.Header>
                            <Card.Body>
                              <div className="mb-3">
                                <strong>Payment Progress:</strong>
                                <div className="progress mt-2" style={{ height: "25px" }}>
                                  <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${(feeRecord.paidAmount / feeRecord.totalFee) * 100}%` }}
                                  >
                                    {((feeRecord.paidAmount / feeRecord.totalFee) * 100).toFixed(1)}%
                                  </div>
                                </div>
                              </div>
                              <div className="mb-3">
                                <strong>Last Payment:</strong>
                                <br />
                                {feeRecord.lastPaymentAmount ? (
                                  <>
                                    <span className="text-success">
                                      {formatFeeCurrency(feeRecord.lastPaymentAmount)}
                                    </span>
                                    <br />
                                    <small className="text-muted">
                                      {feeRecord.lastPaymentDate
                                        ? new Date(feeRecord.lastPaymentDate.seconds * 1000).toLocaleDateString()
                                        : "N/A"
                                      }
                                    </small>
                                  </>
                                ) : (
                                  <span className="text-muted">No payments yet</span>
                                )}
                              </div>
                              {feeRecord.paymentStatus === PAYMENT_STATUS.FULLY_PAID && (
                                <div className="text-center">
                                  <Badge bg="success" className="p-2">
                                    ✓ Fully Paid
                                  </Badge>
                                </div>
                              )}
                            </Card.Body>
                          </Card>
                        </div>
                      </div>

                      {/* Payment History */}
                      {paymentHistory.length > 0 && (
                        <Card>
                          <Card.Header>
                            <h6 className="mb-0">💸 Payment History</h6>
                          </Card.Header>
                          <Card.Body>
                            <div className="table-responsive">
                              <table className="table table-striped table-hover">
                                <thead className="table-light">
                                  <tr>
                                    <th>#</th>
                                    <th>Payment Date</th>
                                    <th>Installment</th>
                                    <th>Amount</th>
                                    <th>Payment Method</th>
                                    <th>Transaction ID</th>
                                    <th>Notes</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paymentHistory.map((payment, index) => (
                                    <tr key={payment.id}>
                                      <td>{index + 1}</td>
                                      <td>
                                        {payment.paymentDate
                                          ? new Date(payment.paymentDate).toLocaleDateString()
                                          : "N/A"
                                        }
                                      </td>
                                      <td>
                                        <Badge bg="info">
                                          Installment {payment.installmentNumber}
                                        </Badge>
                                      </td>
                                      <td>
                                        <strong className="text-success">
                                          {formatFeeCurrency(payment.amount)}
                                        </strong>
                                      </td>
                                      <td>
                                        <Badge bg="secondary">
                                          {payment.paymentMethod.replace(/_/g, " ").toUpperCase()}
                                        </Badge>
                                      </td>
                                      <td>
                                        <small className="text-muted">
                                          {payment.transactionId}
                                        </small>
                                      </td>
                                      <td>
                                        <small>{payment.notes || "-"}</small>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Card.Body>
                        </Card>
                      )}

                      {/* Payment Instructions */}
                      {feeRecord.paymentStatus !== PAYMENT_STATUS.FULLY_PAID && (
                        <Card className="mt-3 border-warning">
                          <Card.Header className="bg-warning">
                            <h6 className="mb-0">📋 Payment Instructions</h6>
                          </Card.Header>
                          <Card.Body>
                            <p className="mb-2">
                              <strong>To make a payment, please contact the admin office with the following details:</strong>
                            </p>
                            <ul>
                              <li>Your Name: <strong>{user?.name}</strong></li>
                              <li>Your Email: <strong>{user?.email}</strong></li>
                              <li>Next Installment Amount: <strong>{formatFeeCurrency(feeRecord.installmentAmount)}</strong></li>
                              <li>Installment Number: <strong>{feeRecord.installmentsPaid + 1} of {feeRecord.maxInstallments}</strong></li>
                            </ul>
                            <p className="mb-0 text-muted">
                              <small>
                                Payment can be made through Cash, Bank Transfer, UPI, or Card.
                                Please collect the payment receipt from the admin after payment.
                              </small>
                            </p>
                          </Card.Body>
                        </Card>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-muted py-5">
                      <h5>No Fee Record Found</h5>
                      <p>Please contact your administrator to initialize your fee record.</p>
                      <small>
                        Fee records are automatically created when you are assigned as a paid trainee or intern.
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Tab>
          )}

        {/* Salary Tab - Only for employees */}
        {user?.studentLevel === STUDENT_LEVELS.EMPLOYEE && (
          <Tab
            eventKey="salary"
            title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><BookOpen size={18} /> Salary</span>}
          >
            <Card className="shadow">
              <Card.Body>
                <Card.Title>💰 My Salary Information</Card.Title>

                {salaryLoading ? (
                  <div className="text-center py-4">
                    <Loader />
                  </div>
                ) : salaryConfig ? (
                  <>
                    {/* Salary Configuration */}
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <Card className="h-100">
                          <Card.Header>
                            <h6 className="mb-0">📊 Salary Summary</h6>
                          </Card.Header>
                          <Card.Body>
                            {salaryHistory.length > 0 ? (
                              <>
                                <div className="mb-2">
                                  <strong>Total Records:</strong>{" "}
                                  <Badge bg="info">{salaryHistory.length}</Badge>
                                </div>
                                <div className="mb-2">
                                  <strong>Total Paid:</strong>{" "}
                                  ₹{salaryHistory
                                    .filter(s => s.status === 'paid')
                                    .reduce((sum, s) => sum + (s.paidAmount || s.netSalary || 0), 0)
                                    .toLocaleString('en-IN')}
                                </div>
                                <div className="mb-2">
                                  <strong>Pending Amount:</strong>{" "}
                                  ₹{salaryHistory
                                    .filter(s => s.status === 'pending')
                                    .reduce((sum, s) => sum + (s.netSalary || 0), 0)
                                    .toLocaleString('en-IN')}
                                </div>
                                <div className="mb-2">
                                  <strong>Last Payment:</strong>{" "}
                                  {salaryHistory.find(s => s.status === 'paid')
                                    ? `₹${salaryHistory.find(s => s.status === 'paid').netSalary?.toLocaleString('en-IN') || 0}`
                                    : 'No payments yet'
                                  }
                                </div>
                              </>
                            ) : (
                              <div className="text-center text-muted">
                                <p>No salary records found</p>
                                <small>Salary records will appear here once generated by admin</small>
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    </div>

                    {/* Salary History */}
                    {salaryHistory.length > 0 && (
                      <Card>
                        <Card.Header>
                          <h6 className="mb-0">📈 Recent Salary History</h6>
                        </Card.Header>
                        <Card.Body>
                          <div className="table-responsive">
                            <table className="table table-sm table-hover">
                              <thead className="table-light">
                                <tr>
                                  <th>Period</th>
                                  <th>Working Days</th>
                                  <th>Total Hours</th>
                                  <th>Gross Salary</th>
                                  <th>Net Salary</th>
                                  <th>Status</th>
                                  <th>Paid Date</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salaryHistory.map((record) => (
                                  <tr key={record.id}>
                                    <td>
                                      <strong>
                                        {new Date(2024, record.month - 1).toLocaleDateString('en-US', { month: 'long' })} {record.year}
                                      </strong>
                                    </td>
                                    <td>{record.workingDays || 0}</td>
                                    <td>{record.totalHours || 0} hrs</td>
                                    <td>₹{(record.grossSalary || 0).toLocaleString('en-IN')}</td>
                                    <td><strong>₹{(record.netSalary || 0).toLocaleString('en-IN')}</strong></td>
                                    <td>
                                      <Badge
                                        bg={
                                          record.status === 'paid' ? 'success' :
                                            record.status === 'pending' ? 'warning' :
                                              record.status === 'overdue' ? 'danger' : 'secondary'
                                        }
                                      >
                                        {record.status?.toUpperCase()}
                                      </Badge>
                                    </td>
                                    <td>
                                      {record.paidAt
                                        ? new Date(record.paidAt).toLocaleDateString()
                                        : '-'
                                      }
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {salaryHistory.length >= 6 && (
                            <div className="text-center mt-3">
                              <small className="text-muted">
                                Showing last 6 records. Visit Salary page for complete history.
                              </small>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    )}
                  </>
                ) : (
                  <div className="text-center text-muted py-5">
                    <h5>No Salary Configuration Found</h5>
                    <p>Please contact your administrator to set up your salary configuration.</p>
                    <small>Once configured, your salary information and history will appear here.</small>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Tab>
        )}

        <Tab
          eventKey="attendance"
          title={<span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Clock size={18} /> Monthly Attendance</span>}
        >
          {/* Monthly Attendance Table */}
          {renderMonthlyAttendanceTable()}
        </Tab>
      </Tabs>

      <Modal
        show={Boolean(selectedTaskDetails)}
        onHide={() => setSelectedTaskDetails(null)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedTaskDetails?.taskName || "Task Details"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTaskDetails && (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3">
                {selectedTaskDetails.projectName && (
                  <Badge bg="info" className="text-white">
                    {selectedTaskDetails.projectName}
                  </Badge>
                )}
                <Badge bg="secondary">
                  {(selectedTaskDetails.priority || "medium").toUpperCase()}
                </Badge>
                <Badge
                  bg={
                    selectedTaskDetails.status === "completed"
                      ? "success"
                      : selectedTaskDetails.status === "in_progress"
                      ? "warning"
                      : "secondary"
                  }
                >
                  {selectedTaskDetails.status?.replace("_", " ").toUpperCase() || "NOT STARTED"}
                </Badge>
              </div>
              <h6 className="fw-semibold">Description</h6>
              <div className="border rounded p-3 bg-light mb-3">
                <TaskDescriptionView text={selectedTaskDetails.taskDescription} />
              </div>
              {selectedTaskDetails.taskImageUrl && (
                <Button
                  as="a"
                  href={selectedTaskDetails.taskImageUrl}
                  target="_blank"
                  rel="noreferrer"
                  download={selectedTaskDetails.taskImageName || "task-image"}
                  variant="outline-primary"
                  size="sm"
                  className="d-inline-flex align-items-center gap-1"
                >
                  <Download size={14} />
                  Download image
                </Button>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setSelectedTaskDetails(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
